<script lang="ts" context="module">
    import { persisted } from "svelte-local-storage-store"
    // Superset of store to make it save to LS
    const INSTALL_KEY = "installedSkipped"
    export const installSkipped = persisted(INSTALL_KEY, false)
    // @ts-ignore
    window.installEnable = () => installSkipped.set(false)

    // Override everything for manual pop ups install instructions
    const overrideOpened = writable(false)
    export function openInstall(goscreen: "install" | "invite" = "install") {
        overrideOpened.set(true)
        screen.set(goscreen)
    }

    export function closeInstall() {
        overrideOpened.set(false)
    }

    export let screen = writable<"invite" | "install" | "close">("invite")
</script>

<script lang="ts">
    import Button from "@components/button/Button.svelte"
    import Icon from "@components/material/Icon.svelte"
    import { fade } from "svelte/transition"
    import { writable } from "svelte/store"
    import AppStoreIcon from "@components/app-store-icons/AppStoreIcon.svelte"
    import GooglePlayIcon from "@components/app-store-icons/GooglePlayIcon.svelte"
    import { NativeiOSLink, NativeAndroidLink } from "@utils/native-app-info"

    /**
     * This lets us know if we should show the invite prompt
     * on startup
     */
    export let showInstallPrompt: boolean

    function skipInstall() {
        closeInstall()
        $installSkipped = true
    }
</script>

{#if showInstallPrompt || $overrideOpened}
    <div
        class="install-overlay secondaryFont"
        in:fade={{ duration: $overrideOpened ? 400 : 0 }}
        out:fade={{ duration: 400, delay: 400 }}
    >
        <div class="install-shell" in:fade={{ delay: 400 }} out:fade>
            {#if $screen == "invite"}
                <section
                    class="pwainstall-popup themed-box"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="install-title"
                >
                    <div class="install-card-header" aria-hidden="true">
                        <img src="/images/bg/04.webp" alt="" />
                        <div class="install-brand">
                            <span>Install</span>
                            <strong>A Course in Miracles CE</strong>
                        </div>
                    </div>

                    <div class="install-card-body">
                        <section class="install-copy">
                            <span class="install-step">Install to your device</span>
                            <h2 id="install-title">Use it like an app</h2>
                            <p>
                                You can keep reading in a browser, but installing ACIM CE gives you
                                a quieter mobile flow and the best experience for practice
                                reminders.
                            </p>
                        </section>

                        <div class="store-actions" aria-label="Native app store links">
                            <a href={NativeiOSLink} aria-label="Download on the App Store">
                                <AppStoreIcon />
                            </a>
                            <a href={NativeAndroidLink} aria-label="Get it on Google Play">
                                <GooglePlayIcon />
                            </a>
                        </div>

                        <div class="install-actions">
                            <Button
                                color="purple-soft"
                                fullwidth
                                icon="install-sm"
                                go={() => ($screen = "install")}
                            >
                                Other installation options
                            </Button>
                            <Button color="purple" text go={skipInstall}>
                                Continue in browser
                            </Button>
                        </div>
                    </div>
                </section>
            {:else if $screen == "install"}
                <section
                    class="pwainstall-popup themed-box normal-rendering"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="install-title"
                >
                    <div class="install-card-body">
                        <section class="install-copy">
                            <span class="install-step">Progressive Web App</span>
                            <h2 id="install-title">Other installation instructions</h2>
                            <p>
                                You can install the app as a <i>Progressive Web App</i> on almost any
                                device. Here are instructions for MacOS and Windows.
                            </p>

                            <ul class="instruction-list">
                                <li>
                                    <b>Windows</b>: Make sure you're using <b>Microsoft Edge</b> to
                                    view this website. Press the
                                    <img
                                        class="inline h-4"
                                        src="/images/app-available-icon.png"
                                        alt="app available icon"
                                    /> icon on the right side of the address bar.
                                </li>
                                <li>
                                    <b>MacOS</b>: Make sure you're using <b>Google Chrome</b> to
                                    view this website. Press the
                                    <img
                                        class="inline h-4"
                                        src="/images/chrome-pwa-icon.png"
                                        alt="app available icon"
                                    /> icon on the right side of the address bar.
                                </li>
                            </ul>

                            <p>Close this window when you're done.</p>
                        </section>

                        <div class="install-actions">
                            <Button color="purple-soft" fullwidth go={() => ($screen = "invite")}>
                                Back to app stores
                            </Button>
                            <Button color="purple" text go={skipInstall}>Nevermind</Button>
                        </div>
                    </div>
                </section>
            {:else}
                <section
                    class="pwainstall-popup themed-box"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="install-title"
                >
                    <div class="install-card-body">
                        <section class="install-copy install-copy--centered">
                            <span class="install-icon"><Icon icon="checkmark" /></span>
                            <span class="install-step">Ready</span>
                            <h2 id="install-title">Enjoy!</h2>
                            <p>Please close this window and open the app from your home screen.</p>
                        </section>

                        <div class="install-actions">
                            <Button color="purple-soft" fullwidth go={() => ($screen = "install")}>
                                Back to instructions
                            </Button>
                        </div>
                    </div>
                </section>
            {/if}
        </div>
    </div>
{/if}

<style>
    .install-overlay {
        --install-bg: var(--background);
        --install-text: var(--color-grey-900);
        --install-muted: var(--color-grey-600);
        --install-accent: var(--color-purple-700);
        position: fixed;
        inset: 0;
        z-index: 22;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        min-width: 0;
        min-height: 0;
        max-width: 100%;
        max-height: 100%;
        overflow: hidden;
        background: var(--install-bg);
        color: var(--install-text);
        isolation: isolate;
    }

    .install-overlay,
    .install-overlay * {
        box-sizing: border-box;
    }

    .install-overlay :global(header),
    .install-overlay :global(main),
    .install-overlay :global(footer) {
        display: block;
        grid-area: auto;
        width: auto;
        height: auto;
        min-height: 0;
        position: static;
    }

    :global(.darkmode) .install-overlay {
        --install-bg: var(--background);
        --install-text: var(--color-grey-0);
        --install-muted: var(--color-grey-200);
        --install-accent: var(--color-gold-300);
    }

    .install-shell {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        min-height: 0;
        margin: 0 auto;
        overflow: hidden;
        padding: calc(var(--safe-area-inset-top, 0rem) + 0.9rem)
            max(1rem, var(--safe-area-inset-right, 0rem))
            calc(var(--safe-area-inset-bottom, 0rem) + 0.9rem)
            max(1rem, var(--safe-area-inset-left, 0rem));
    }

    .pwainstall-popup {
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 34rem;
        max-height: 100%;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
    }

    .install-card-header {
        position: relative;
        flex: 0 0 auto;
        height: clamp(8.5rem, 30vh, 13rem);
        min-width: 0;
        overflow: hidden;
        background: var(--color-purple-50);
    }

    .install-card-header::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(
            180deg,
            rgb(41 34 74 / 0.08) 0%,
            rgb(41 34 74 / 0.24) 54%,
            rgb(41 34 74 / 0.72) 100%
        );
        pointer-events: none;
    }

    :global(.darkmode) .install-card-header {
        background: var(--color-grey-900);
    }

    .install-card-header img {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }

    .install-brand {
        position: absolute;
        right: 1rem;
        bottom: 1rem;
        left: 1rem;
        z-index: 1;
        display: grid;
        gap: 0.25rem;
        min-width: 0;
        color: var(--color-grey-0);
    }

    .install-brand span,
    .install-step {
        color: var(--install-accent);
        font-size: 0.72rem;
        font-weight: 750;
        letter-spacing: 0;
        line-height: 1;
        text-transform: uppercase;
    }

    .install-brand span {
        color: var(--color-gold-300);
    }

    .install-brand strong {
        display: block;
        overflow: hidden;
        color: var(--color-grey-0);
        font-size: 1rem;
        font-weight: 750;
        line-height: 1.2;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .install-card-body {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
        padding: 1rem;
    }

    .install-copy {
        display: block;
        min-width: 0;
        min-height: 0;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        touch-action: pan-y;
        -webkit-overflow-scrolling: touch;
        padding-right: 0.2rem;
    }

    .install-copy--centered {
        text-align: center;
    }

    .install-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 3rem;
        height: 3rem;
        margin-bottom: 0.75rem;
        border-radius: var(--radius-button);
        background: var(--color-purple-50);
        color: var(--color-purple-700);
    }

    .install-icon :global(svg) {
        width: 1.35rem;
        height: 1.35rem;
    }

    :global(.darkmode) .install-icon {
        background: var(--color-grey-700);
        color: var(--color-purple-100);
    }

    .install-step + h2 {
        margin-top: 0.8rem;
    }

    .pwainstall-popup h2 {
        margin: 0;
        color: var(--install-text);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1.65rem;
        font-style: normal;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 1.12;
        overflow-wrap: break-word;
    }

    .pwainstall-popup p {
        margin: 0.8rem 0 0;
        color: var(--install-muted);
        font-size: 0.94rem;
        line-height: 1.58;
        overflow-wrap: break-word;
    }

    .store-actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.75rem;
        width: 100%;
    }

    .store-actions a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        max-width: 100%;
        border-radius: 0.6rem;
    }

    .store-actions a:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .store-actions a :global(svg) {
        display: block;
        width: min(10rem, 100%);
        height: auto;
    }

    .install-actions {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.7rem;
        width: 100%;
        min-width: 0;
    }

    .instruction-list {
        display: grid;
        gap: 0.85rem;
        margin: 1rem 0 0;
        padding-left: 1.25rem;
        color: var(--install-muted);
        font-size: 0.94rem;
        line-height: 1.55;
    }

    .instruction-list li {
        padding-left: 0.1rem;
    }

    .instruction-list b {
        color: var(--install-text);
    }

    .pwainstall-popup :global(.app-button) {
        max-width: 30rem;
    }

    .pwainstall-popup :global(.app-button svg) {
        height: 1em;
    }

    @media only screen and (min-width: 46rem) {
        .install-shell {
            padding: calc(var(--safe-area-inset-top, 0rem) + 1.5rem)
                max(2rem, var(--safe-area-inset-right, 0rem))
                calc(var(--safe-area-inset-bottom, 0rem) + 1.5rem)
                max(2rem, var(--safe-area-inset-left, 0rem));
        }

        .install-card-body {
            gap: 1.2rem;
            padding: 1.5rem;
        }

        .pwainstall-popup h2 {
            font-size: 2.1rem;
        }

        .pwainstall-popup p,
        .instruction-list {
            font-size: 1rem;
            line-height: 1.68;
        }
    }

    @media only screen and (max-height: 38rem) {
        .install-shell {
            padding: calc(var(--safe-area-inset-top, 0rem) + 0.55rem)
                max(0.75rem, var(--safe-area-inset-right, 0rem))
                calc(var(--safe-area-inset-bottom, 0rem) + 0.55rem)
                max(0.75rem, var(--safe-area-inset-left, 0rem));
        }

        .install-card-header {
            display: none;
        }

        .install-card-body {
            gap: 0.8rem;
            padding: 0.9rem;
        }

        .pwainstall-popup h2 {
            font-size: 1.35rem;
        }

        .pwainstall-popup p,
        .instruction-list {
            font-size: 0.9rem;
            line-height: 1.45;
        }
    }
</style>
